import { create } from 'zustand';

type OnYourHonorState = {
  /**
   * Running cursor into `ON_YOUR_HONOR_CONFIRMATIONS`, advanced each time a mark
   * opens the thank-you reason sheet so consecutive marks rotate through the
   * lines instead of repeating. In-memory only — rotation is a session nicety,
   * not state worth persisting.
   *
   * The marked state itself is no longer held here: it now lives on the server
   * as `inquiry.title.onYourHonor` and is read/written through the inquiry query
   * cache (see useOnYourHonor). This store only owns the rotation.
   */
  rotationCursor: number;
  /** Advances the rotation and returns the index to show for this mark. */
  advanceConfirmation: () => number;
  /**
   * Conversations that have already fired the submitted attestation this session.
   * Session-scoped, not persisted — its only job is to keep the submitted event
   * to once per inquiry (counting attestations, not keystrokes).
   */
  submittedConversationIds: Set<string>;
  /**
   * Records a submission for a conversation and reports whether it was the first
   * this session. Returns `true` once per conversation (fire the event), `false`
   * on every send after (skip it).
   */
  registerSubmission: (conversationId: string) => boolean;
};

export const useOnYourHonorStore = create<OnYourHonorState>()((set, get) => ({
  rotationCursor: 0,
  advanceConfirmation: () => {
    const index = get().rotationCursor;

    set({ rotationCursor: index + 1 });

    return index;
  },
  submittedConversationIds: new Set<string>(),
  registerSubmission: (conversationId) => {
    const submitted = get().submittedConversationIds;

    if (submitted.has(conversationId)) {
      return false;
    }

    set({ submittedConversationIds: new Set(submitted).add(conversationId) });

    return true;
  },
}));
